import React, {Suspense, useEffect, useState, useTransition} from "react";

import {useParams} from "react-router";

import {useRelayEnvironment} from "react-relay";
import {Environment} from "relay-runtime";
import Waiting from "./presentational/Waiting";
import DelayedFallback from "./presentational/DelayedFallback";

const SUSPENSE_CONFIG = {timeoutMs: 5000};

type PrepareFunction = (params: object, relayEnvironment: Environment) => any;

export declare function prepare(params: object, relayEnvironment: Environment);

type TransitioningContainerProps = {
    component: any,
    prepare: PrepareFunction,
    innerProps?: object
};

const TransitioningContainer: React.FC<TransitioningContainerProps> = (props) => {
    // Improve the route transition UX by delaying transitions: show the previous route entry
    // for a brief period while the next route is being prepared. See
    // <https://reactjs.org/docs/concurrent-mode-patterns.html#transitions
    //
    // Ignore TS warning about useTransition() argument, because the TS definition seems to be
    // wrong in the current React 18 alpha
    // @ts-ignore
    const [, startTransition] = useTransition(SUSPENSE_CONFIG);
    const [visibleComponent, setVisibleComponent] = useState<React.ReactElement | null>(null);
    const [currentComponentType, setCurrentComponentType] = useState<any>(null);
    const params = useParams();
    const environment = useRelayEnvironment();
    const {prepare, innerProps} = props;
    const Component = props.component;

    // Detect component type change and reset state immediately
    // This prevents stale outlet context from previous routes
    const componentTypeChanged = currentComponentType !== null && currentComponentType !== Component;
    if (componentTypeChanged) {
        // Synchronous state reset during render - this is intentional to prevent
        // the old component from being rendered even once with new route context.
        // Note: use the functional updater form so that plain function components are stored
        // as-is. Passing a function value directly to a state setter would be interpreted as
        // an updater and invoke the component, breaking the type comparison (infinite loop).
        setVisibleComponent(null);
        setCurrentComponentType(() => Component);
    }

    // On mount and route changes
    useEffect(() => {
        // Track the component type
        if (currentComponentType !== Component) {
            setCurrentComponentType(() => Component);
        }

        // Start loading the new component
        startTransition(() => {
            const prepared = prepare(params, environment);
            setVisibleComponent(<Component prepared={prepared} params={params} {...innerProps} />)
        });
    }, [params, Component, innerProps, environment, prepare, currentComponentType]);

    const delayedWaiting = (
        <DelayedFallback delay={200}>
            <Waiting/>
        </DelayedFallback>
    );

    return (
        <Suspense fallback={delayedWaiting}>
            {visibleComponent ?? delayedWaiting}
        </Suspense>
    )
}

export default TransitioningContainer;
